import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer, Box, Typography, IconButton, Button,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useMedicationCart, CartItem } from '../../contexts/MedicationCartContext';


interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartItemRow: React.FC<{
  item: CartItem;
  onDecrement: () => void;
  onIncrement: () => void;
  onRemove: () => void;
}> = ({ item, onDecrement, onIncrement, onRemove }) => {
  const lineTotal = (Number(item.price) * item.quantity).toFixed(2);
  return (
    <Box
      sx={{
        bgcolor: 'primary.softBg',
        borderRadius: '10px',
        px: 1.5,
        py: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} color="text.headline" noWrap>
            {item.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">{item.chainName}</Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Eliminar del carrito"
          onClick={onRemove}
          sx={{ color: '#E53935', ml: 1, mt: -0.5 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            title={`Disminuir cantidad de ${item.name}`}
            disabled={item.quantity <= 1}
            onClick={onDecrement}
            sx={{
              border: '1px solid #D7E1EF',
              borderRadius: '8px',
              width: 28,
              height: 28,
              color: 'text.headline',
            }}
          >
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 22, textAlign: 'center', color: 'text.headline' }}>
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            title={`Aumentar cantidad de ${item.name}`}
            onClick={onIncrement}
            sx={{
              border: '1px solid #D7E1EF',
              borderRadius: '8px',
              width: 28,
              height: 28,
              color: 'text.headline',
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Typography variant="caption" color="text.secondary">
          RD$ {item.price} × {item.quantity} = RD$ {lineTotal}
        </Typography>
      </Box>
    </Box>
  );
};

export const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, itemCount } = useMedicationCart();
  const total = items.reduce((sum, i) => sum + Number(i.price) * i.quantity, 0);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          maxHeight: '75vh',
          px: 2.5,
          pt: 2,
          paddingBottom: { xs: 'calc(16px + env(safe-area-inset-bottom))', md: 2 },
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <Box sx={{ width: 40, height: 4, background: '#E1E8ED', borderRadius: 2 }} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.headline">
          Carrito ({itemCount})
        </Typography>
        {itemCount > 0 && (
          <Typography
            variant="body2"
            fontWeight={600}
            onClick={clearCart}
            sx={{ color: '#E53935', cursor: 'pointer' }}
          >
            Limpiar
          </Typography>
        )}
      </Box>

      {itemCount === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.disabled">
            Tu carrito está vacío.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              overflowY: 'auto',
              mb: 2,
              flex: 1,
            }}
          >
            {items.map((item) => (
              <CartItemRow
                key={item.pharmacyProductId}
                item={item}
                onDecrement={() => updateQuantity(item.pharmacyProductId, item.quantity - 1)}
                onIncrement={() => updateQuantity(item.pharmacyProductId, item.quantity + 1)}
                onRemove={() => removeItem(item.pharmacyProductId)}
              />
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
              pt: 1.5,
              borderTop: '1px solid #E1E8ED',
            }}
          >
            <Typography variant="body2" fontWeight={600} color="text.headline">Subtotal</Typography>
            <Typography variant="body1" fontWeight={700} color="primary.main">
              RD$ {total.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        disabled={itemCount === 0}
        onClick={handleCheckout}
        sx={{
          borderRadius: '10px',
          py: 1.5,
          fontWeight: 700,
          fontSize: 15,
          textTransform: 'none',
          '&.Mui-disabled': { background: '#E1E8ED', color: '#A0AEC0' },
        }}
      >
        Ir al checkout
      </Button>
    </Drawer>
  );
};
